/**
 * Dulux Marble — Renovation Quote Estimator
 * CRITICAL: No price is ever shown, calculated, or referenced at any point.
 */
(function () {
  'use strict';

  // ─── State ────────────────────────────────────────────────
  var state = {
    currentStep: 1,
    totalSteps: 6,
    selections: {
      spaceType:    null,   // 'kitchen' | 'bathroom' | 'both'
      layout:       null,   // layout key
      measurements: {},     // { A: 0, B: 0, ... } keyed by label, values in mm
      areaSqm:      0,      // calculated display value — never used for pricing
      stone:        null,   // { id, title, handle, surface, image }
      extras:       {},     // { 'undermount-sink': 1, 'cooktop': 2, ... }
      edgeProfile:  'pencil-round',
    },
    softLead:         null,   // { name, email } saved to localStorage
    partialLeadSent:  false,
    formSubmitted:    false,
    products:         [],     // fetched once, then cached
    activeFilter:     'all',
  };

  // ─── Config (hardcoded; overridable via data-* on #estimator-app) ──
  var config = {
    emailjsServiceId:           'service_9q866ak',
    emailjsInternalTemplateId:  'template_5ypwemu',
    emailjsCustomerTemplateId:  'template_o3bj7bh',
    emailjsPublicKey:           '6SLt389pQFa_jeDS8',
    webhookUrl:                 'https://script.google.com/macros/s/AKfycbw0YJrp2A3HIDfb0uYoHjZQBRqGm_kZp_sklpP2uhrgZfmd5ZFnG-iwrXfyX9Bmtxnb/exec',
  };

  // ─── Step labels ──────────────────────────────────────────
  var STEP_NAMES = ['', 'Space type', 'Layout', 'Measurements', 'Stone selection', 'Extras', 'Your details'];

  // --- Step micro-motivators -------------------------------------------
  var STEP_MOTIVATORS = {
    2: "Almost there — just 4 more steps.",
    3: "Halfway through — you're doing great.",
    4: "This is the fun part — choose your stone.",
    5: "Nearly done!",
    6: "Last step"
  };

  // ─── Layout data ──────────────────────────────────────────
  var LAYOUT_DATA = {
    'straight': {
      label: 'Straight',
      description: 'Single bench run',
      thumbViewBox: '0 0 120 60',
      thumbSvg: '<rect x="10" y="22" width="100" height="16" stroke="currentColor" stroke-width="1.5" fill="none" rx="1"/>',
      planViewBox: '0 0 280 100',
      planSvg: [
        '<rect x="30" y="35" width="220" height="30" stroke="currentColor" stroke-width="1.5" fill="none" rx="1"/>',
        '<line x1="30" y1="29" x2="250" y2="29" stroke="currentColor" stroke-width="0.75" stroke-dasharray="3,3" opacity="0.45"/>',
        '<line x1="30" y1="25" x2="30" y2="33" stroke="currentColor" stroke-width="0.75" opacity="0.45"/>',
        '<line x1="250" y1="25" x2="250" y2="33" stroke="currentColor" stroke-width="0.75" opacity="0.45"/>',
        '<text x="140" y="20" class="est-fp-label" text-anchor="middle">A</text>',
        '<line x1="258" y1="37" x2="258" y2="63" stroke="currentColor" stroke-width="0.75" stroke-dasharray="3,3" opacity="0.45"/>',
        '<line x1="254" y1="37" x2="262" y2="37" stroke="currentColor" stroke-width="0.75" opacity="0.45"/>',
        '<line x1="254" y1="63" x2="262" y2="63" stroke="currentColor" stroke-width="0.75" opacity="0.45"/>',
        '<text x="270" y="53" class="est-fp-label" text-anchor="start">W</text>',
      ].join(''),
      segments: [
        { key: 'A', label: 'Bench length' },
        { key: 'W', label: 'Bench width' },
      ],
      calcArea: function (m) {
        return (m.A * (m.W || 600)) / 1e6;
      }
    },

    'galley': {
      label: 'Galley',
      description: 'Two parallel runs',
      thumbViewBox: '0 0 120 80',
      thumbSvg: [
        '<rect x="10" y="12" width="100" height="16" stroke="currentColor" stroke-width="1.5" fill="none" rx="1"/>',
        '<rect x="10" y="52" width="100" height="16" stroke="currentColor" stroke-width="1.5" fill="none" rx="1"/>',
      ].join(''),
      planViewBox: '0 0 280 160',
      planSvg: [
        '<rect x="30" y="20" width="220" height="30" stroke="currentColor" stroke-width="1.5" fill="none" rx="1"/>',
        '<rect x="30" y="110" width="220" height="30" stroke="currentColor" stroke-width="1.5" fill="none" rx="1"/>',
        '<text x="140" y="12" class="est-fp-label" text-anchor="middle">A</text>',
        '<text x="140" y="155" class="est-fp-label" text-anchor="middle">B</text>',
        '<line x1="258" y1="22" x2="258" y2="48" stroke="currentColor" stroke-width="0.75" stroke-dasharray="3,3" opacity="0.45"/>',
        '<line x1="254" y1="22" x2="262" y2="22" stroke="currentColor" stroke-width="0.75" opacity="0.45"/>',
        '<line x1="254" y1="48" x2="262" y2="48" stroke="currentColor" stroke-width="0.75" opacity="0.45"/>',
        '<text x="270" y="37" class="est-fp-label" text-anchor="start">W</text>',
      ].join(''),
      segments: [
        { key: 'A', label: 'Front bench length' },
        { key: 'B', label: 'Rear bench length' },
        { key: 'W', label: 'Bench width' },
      ],
      calcArea: function (m) {
        return ((m.A + m.B) * (m.W || 600)) / 1e6;
      }
    },

    'l-shape': {
      label: 'L-shape',
      description: 'Two runs at 90°',
      thumbViewBox: '0 0 100 100',
      thumbSvg: '<path d="M10,10 H90 V26 H26 V90 H10 Z" stroke="currentColor" stroke-width="1.5" fill="none"/>',
      planViewBox: '0 0 280 280',
      planSvg: [
        '<path d="M30,20 H250 V52 H62 V260 H30 Z" stroke="currentColor" stroke-width="1.5" fill="none"/>',
        '<text x="140" y="12" class="est-fp-label" text-anchor="middle">A</text>',
        '<text x="70" y="162" class="est-fp-label" text-anchor="start">B</text>',
        '<line x1="258" y1="22" x2="258" y2="50" stroke="currentColor" stroke-width="0.75" stroke-dasharray="3,3" opacity="0.45"/>',
        '<line x1="254" y1="22" x2="262" y2="22" stroke="currentColor" stroke-width="0.75" opacity="0.45"/>',
        '<line x1="254" y1="50" x2="262" y2="50" stroke="currentColor" stroke-width="0.75" opacity="0.45"/>',
        '<text x="270" y="38" class="est-fp-label" text-anchor="start">W</text>',
      ].join(''),
      segments: [
        { key: 'A', label: 'Horizontal bench length' },
        { key: 'B', label: 'Side bench length' },
        { key: 'W', label: 'Bench width' },
      ],
      calcArea: function (m) {
        var w = m.W || 600;
        return ((m.A + m.B) * w) / 1e6;
      }
    },

    'u-shape': {
      label: 'U-shape',
      description: 'Three runs, three walls',
      thumbViewBox: '0 0 100 100',
      thumbSvg: '<path d="M10,10 H26 V74 H74 V10 H90 V90 H10 Z" stroke="currentColor" stroke-width="1.5" fill="none"/>',
      planViewBox: '0 0 280 280',
      planSvg: [
        '<path d="M30,20 H62 V218 H218 V20 H250 V250 H30 Z" stroke="currentColor" stroke-width="1.5" fill="none"/>',
        '<text x="15" y="137" class="est-fp-label" text-anchor="end">A</text>',
        '<text x="140" y="269" class="est-fp-label" text-anchor="middle">B</text>',
        '<text x="265" y="137" class="est-fp-label" text-anchor="start">C</text>',
        '<line x1="32" y1="10" x2="60" y2="10" stroke="currentColor" stroke-width="0.75" stroke-dasharray="3,3" opacity="0.45"/>',
        '<line x1="30" y1="5" x2="30" y2="15" stroke="currentColor" stroke-width="0.75" opacity="0.45"/>',
        '<line x1="62" y1="5" x2="62" y2="15" stroke="currentColor" stroke-width="0.75" opacity="0.45"/>',
        '<text x="46" y="19" class="est-fp-label" text-anchor="middle">W</text>',
      ].join(''),
      segments: [
        { key: 'A', label: 'Left bench length' },
        { key: 'B', label: 'Back bench length' },
        { key: 'C', label: 'Right bench length' },
        { key: 'W', label: 'Bench width' },
      ],
      calcArea: function (m) {
        var w = m.W || 600;
        return ((m.A + m.B + m.C - 2 * w) * w) / 1e6;
      }
    },

    'island': {
      label: 'Island',
      description: 'L-shape plus island bench',
      thumbViewBox: '0 0 100 100',
      thumbSvg: [
        '<path d="M10,10 H90 V26 H26 V68 H10 Z" stroke="currentColor" stroke-width="1.5" fill="none"/>',
        '<rect x="28" y="40" width="60" height="14" stroke="currentColor" stroke-width="1.5" fill="none" rx="1"/>',
      ].join(''),
      planViewBox: '0 0 280 260',
      planSvg: [
        '<path d="M30,20 H250 V52 H62 V160 H30 Z" stroke="currentColor" stroke-width="1.5" fill="none"/>',
        '<rect x="80" y="90" width="150" height="36" stroke="currentColor" stroke-width="1.5" fill="none" rx="1"/>',
        '<text x="140" y="12" class="est-fp-label" text-anchor="middle">A</text>',
        '<text x="4" y="94" class="est-fp-label" text-anchor="start">B</text>',
        '<text x="155" y="83" class="est-fp-label" text-anchor="middle">C</text>',
        '<text x="238" y="113" class="est-fp-label" text-anchor="start">D</text>',
        '<line x1="258" y1="22" x2="258" y2="50" stroke="currentColor" stroke-width="0.75" stroke-dasharray="3,3" opacity="0.45"/>',
        '<line x1="254" y1="22" x2="262" y2="22" stroke="currentColor" stroke-width="0.75" opacity="0.45"/>',
        '<line x1="254" y1="50" x2="262" y2="50" stroke="currentColor" stroke-width="0.75" opacity="0.45"/>',
        '<text x="270" y="38" class="est-fp-label" text-anchor="start">W</text>',
      ].join(''),
      segments: [
        { key: 'A', label: 'Main bench length' },
        { key: 'B', label: 'Side bench length' },
        { key: 'C', label: 'Island length' },
        { key: 'D', label: 'Island width' },
        { key: 'W', label: 'Bench width' },
      ],
      calcArea: function (m) {
        var w = m.W || 600;
        return ((m.A + m.B) * w + m.C * m.D) / 1e6;
      }
    }
  };

  // ─── Stone filter definitions ─────────────────────────────
  var STONE_FILTERS = [
    { key: 'all',       label: 'All',           test: null },
    { key: 'heritage',  label: 'Heritage',       test: function (p) { return p.tags.includes('heritage'); } },
    { key: 'statement', label: 'Statement',      test: function (p) { return p.tags.includes('statement'); } },
    { key: 'mirror',    label: 'Mirror',         test: function (p) { return p.tags.some(function (t) { return t.toLowerCase() === 'mirror'; }); } },
    { key: 'light',     label: 'Light & White',  test: function (p) { return p.tags.includes('light'); } },
    { key: 'beige',     label: 'Natural tones',  test: function (p) { return p.tags.includes('beige'); } },
    { key: 'wooden',    label: 'Timber',         test: function (p) { return p.tags.some(function (t) { return t.toLowerCase() === 'wooden'; }); } },
    { key: 'terrazzo',  label: 'Terrazzo',       test: function (p) { return p.tags.some(function (t) { return t.toLowerCase() === 'terrazzo'; }); } },
  ];

  // ─── Extras data (step 5) ─────────────────────────────────
  var EXTRAS_DATA = [
    {
      key: 'undermount-sink',
      label: 'Undermount sink',
      icon: '<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><rect x="4" y="8" width="32" height="24" rx="2" stroke="currentColor" stroke-width="1.5" fill="none"/><ellipse cx="20" cy="20" rx="9" ry="7" stroke="currentColor" stroke-width="1.5" fill="none"/><line x1="20" y1="8" x2="20" y2="13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>',
    },
    {
      key: 'cooktop',
      label: 'Cooktop cutout',
      icon: '<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><rect x="4" y="8" width="32" height="24" rx="2" stroke="currentColor" stroke-width="1.5" fill="none"/><circle cx="13" cy="15" r="3.5" stroke="currentColor" stroke-width="1.5" fill="none"/><circle cx="27" cy="15" r="3.5" stroke="currentColor" stroke-width="1.5" fill="none"/><circle cx="13" cy="25" r="3.5" stroke="currentColor" stroke-width="1.5" fill="none"/><circle cx="27" cy="25" r="3.5" stroke="currentColor" stroke-width="1.5" fill="none"/></svg>',
    },
    {
      key: 'tap-holes',
      label: 'Tap holes',
      icon: '<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><rect x="4" y="8" width="32" height="24" rx="2" stroke="currentColor" stroke-width="1.5" fill="none"/><circle cx="20" cy="20" r="4" stroke="currentColor" stroke-width="1.5" fill="none"/><circle cx="20" cy="20" r="1.5" fill="currentColor"/></svg>',
    },
    {
      key: 'gpo',
      label: 'GPO cutout',
      icon: '<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><rect x="4" y="8" width="32" height="24" rx="2" stroke="currentColor" stroke-width="1.5" fill="none"/><rect x="12" y="14" width="16" height="12" rx="1" stroke="currentColor" stroke-width="1.5" fill="none"/><line x1="14" y1="20" x2="26" y2="20" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>',
    },
    {
      key: 'drain-groove',
      label: 'Drain groove',
      icon: '<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><rect x="4" y="8" width="32" height="24" rx="2" stroke="currentColor" stroke-width="1.5" fill="none"/><line x1="12" y1="14" x2="12" y2="26" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><line x1="17" y1="14" x2="17" y2="26" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><line x1="22" y1="14" x2="22" y2="26" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><line x1="27" y1="14" x2="27" y2="26" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>',
    },
    {
      key: 'waterfall-panel',
      label: 'Waterfall panel',
      icon: '<svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><rect x="6" y="8" width="28" height="10" rx="1" stroke="currentColor" stroke-width="1.5" fill="none"/><rect x="26" y="18" width="8" height="14" rx="1" stroke="currentColor" stroke-width="1.5" fill="none"/></svg>',
    },
  ];

  // ─── Edge profile data (step 5) ───────────────────────────
  var EDGE_PROFILE_DATA = [
    {
      key: 'pencil-round',
      label: 'Pencil round',
      svg: '<path d="M5,6 H55 Q62,6 62,13 V29 H5 Z" stroke="currentColor" stroke-width="1.5" fill="none"/>',
    },
    {
      key: 'eased',
      label: 'Eased',
      svg: '<path d="M5,6 H58 L62,11 V29 H5 Z" stroke="currentColor" stroke-width="1.5" fill="none"/>',
    },
    {
      key: 'mitred',
      label: 'Mitred',
      svg: '<path d="M5,6 H38 L62,29 H5 Z" stroke="currentColor" stroke-width="1.5" fill="none"/>',
    },
    {
      key: 'bullnose',
      label: 'Bullnose',
      svg: '<path d="M5,6 H52 Q62,6 62,17 Q62,28 52,28 H5 Z" stroke="currentColor" stroke-width="1.5" fill="none"/>',
    },
  ];

  // ════════════════════════════════════════════════════════════
  // INIT
  // ════════════════════════════════════════════════════════════
  function init() {
    var app = document.getElementById('estimator-app');
    if (!app) return;

    // Override defaults from data-* attributes if set in theme settings
    config.emailjsServiceId           = app.dataset.emailjsServiceId           || config.emailjsServiceId;
    config.emailjsInternalTemplateId  = app.dataset.emailjsInternalTemplateId  || config.emailjsInternalTemplateId;
    config.emailjsCustomerTemplateId  = app.dataset.emailjsCustomerTemplateId  || config.emailjsCustomerTemplateId;
    config.emailjsPublicKey           = app.dataset.emailjsPublicKey           || config.emailjsPublicKey;

    if (config.emailjsPublicKey && typeof window.emailjs !== 'undefined') {
      window.emailjs.init(config.emailjsPublicKey);
    }

    state.softLead = getSoftLead();
    setupPartialLeadTrigger();
    bindNavigation();

    var landing = document.getElementById('estimator-landing');
    var inner   = document.getElementById('estimator-inner');

    if (landing && inner) {
      inner.classList.add('estimator-inner--hidden');
      var cta = document.getElementById('estimator-landing-cta');
      if (cta) {
        cta.addEventListener('click', function () {
          landing.classList.add('estimator-landing--hidden');
          inner.classList.remove('estimator-inner--hidden');
          renderStep(1);
        });
      }
    } else {
      renderStep(1);
    }
  }

  // ════════════════════════════════════════════════════════════
  // STEP ROUTER
  // ════════════════════════════════════════════════════════════
  function renderStep(step) {
    state.currentStep = step;

    document.querySelectorAll('.estimator-step').forEach(function (el) {
      el.classList.toggle('estimator-step--hidden', parseInt(el.dataset.step) !== step);
    });

    updateProgressBar(step);
    updateNavButtons(step);

    switch (step) {
      case 1: renderStep1(); break;
      case 2: renderStep2(); break;
      case 3: renderStep3(); break;
      case 4: renderStep4(); break;
      case 5: renderStep5(); break;
      case 6: renderStep6(); break;
    }
  }

  // ════════════════════════════════════════════════════════════
  // STEP 1 — Space type
  // Cards are pre-rendered in Liquid. JS binds events only.
  // ════════════════════════════════════════════════════════════
  function renderStep1() {
    var container = document.getElementById('estimator-space-cards');
    if (!container || container.dataset.bound) return;
    container.dataset.bound = 'true';

    container.querySelectorAll('.estimator-space-card').forEach(function (btn) {
      if (state.selections.spaceType === btn.dataset.value) {
        btn.classList.add('estimator-space-card--selected');
        btn.setAttribute('aria-pressed', 'true');
      }

      btn.addEventListener('click', function () {
        state.selections.spaceType = btn.dataset.value;

        container.querySelectorAll('.estimator-space-card').forEach(function (b) {
          b.classList.remove('estimator-space-card--selected');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('estimator-space-card--selected');
        btn.setAttribute('aria-pressed', 'true');

        var err = document.getElementById('estimator-step1-error');
        if (err) err.classList.add('estimator-step__error--hidden');

        // Auto-advance after selection animation completes
        setTimeout(function () { transitionToStep(1, 2); }, 180);
      });
    });
  }

  // ════════════════════════════════════════════════════════════
  // STEP 2 — Layout selector
  // ════════════════════════════════════════════════════════════
  function renderStep2() {
    renderLayoutCards();
    renderSaveProgress();
  }

  function renderLayoutCards() {
    var container = document.getElementById('estimator-layout-cards');
    if (!container) return;

    var html = Object.keys(LAYOUT_DATA).map(function (key) {
      var def        = LAYOUT_DATA[key];
      var isSelected = state.selections.layout === key;
      return [
        '<button type="button" class="estimator-card estimator-card--layout', isSelected ? ' estimator-card--selected' : '', '"',
        ' data-layout="', key, '"',
        ' aria-pressed="', isSelected ? 'true' : 'false', '"',
        '>',
          '<svg class="estimator-layout-thumb" viewBox="', def.thumbViewBox, '" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">',
            def.thumbSvg,
          '</svg>',
          '<span class="estimator-card__label">', def.label, '</span>',
        '</button>',
      ].join('');
    }).join('');

    container.innerHTML = html;

    container.querySelectorAll('.estimator-card--layout').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var newLayout = btn.dataset.layout;

        if (state.selections.layout !== newLayout) {
          state.selections.layout      = newLayout;
          state.selections.measurements = {};
          state.selections.areaSqm     = 0;
        }

        container.querySelectorAll('.estimator-card--layout').forEach(function (b) {
          b.classList.remove('estimator-card--selected');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('estimator-card--selected');
        btn.setAttribute('aria-pressed', 'true');

        var err = document.getElementById('estimator-step2-error');
        if (err) err.classList.add('estimator-step__error--hidden');

        setTimeout(function () { transitionToStep(2, 3); }, 180);
      });
    });
  }

  function renderSaveProgress() {
    var container = document.getElementById('estimator-save-progress');
    if (!container || container.dataset.rendered) return;
    container.dataset.rendered = 'true';

    if (state.softLead && state.softLead.email) {
      container.innerHTML = '<p class="estimator-save-progress__saved">&#10003; Progress saved for ' + escapeHtml(state.softLead.email) + '</p>';
      return;
    }

    container.innerHTML = [
      '<div class="estimator-save-progress">',
        '<p class="estimator-save-progress__heading">Want to save your progress?</p>',
        '<p class="estimator-save-progress__sub">Enter your email and we\'ll save where you\'re up to.</p>',
        '<div class="estimator-save-progress__fields">',
          '<div class="estimator-field estimator-field--inline">',
            '<label for="est-save-name">First name</label>',
            '<input type="text" id="est-save-name" placeholder="e.g. Sarah" autocomplete="given-name">',
          '</div>',
          '<div class="estimator-field estimator-field--inline">',
            '<label for="est-save-email">Email address</label>',
            '<input type="email" id="est-save-email" placeholder="e.g. sarah@email.com" autocomplete="email">',
          '</div>',
        '</div>',
        '<button type="button" class="estimator-save-progress__btn" id="estimator-save-btn">Save progress</button>',
      '</div>',
    ].join('');

    var saveBtn    = document.getElementById('estimator-save-btn');
    var nameInput  = document.getElementById('est-save-name');
    var emailInput = document.getElementById('est-save-email');

    if (saveBtn) {
      saveBtn.addEventListener('click', function () {
        var email = emailInput ? emailInput.value.trim() : '';
        var name  = nameInput  ? nameInput.value.trim()  : '';
        if (!isValidEmail(email)) {
          if (emailInput) emailInput.focus();
          return;
        }
        state.softLead = { name: name, email: email };
        saveSoftLead(state.softLead);
        container.innerHTML = '<p class="estimator-save-progress__saved">&#10003; Progress saved for ' + escapeHtml(email) + '</p>';
      });
    }
  }

  // ════════════════════════════════════════════════════════════
  // STEP 3 — Dimensions
  // ════════════════════════════════════════════════════════════
  function renderStep3() {
    renderFloorPlan();
    renderDimensionInputs();
  }

  function renderFloorPlan() {
    var container = document.getElementById('estimator-floorplan');
    if (!container) return;

    var def = LAYOUT_DATA[state.selections.layout];
    if (!def) { container.innerHTML = ''; return; }

    container.innerHTML = [
      '<svg class="estimator-floorplan-svg" viewBox="', def.planViewBox, '"',
      ' xmlns="http://www.w3.org/2000/svg" aria-label="', def.label, ' floor plan" role="img">',
        def.planSvg,
      '</svg>',
    ].join('');
  }

  function renderDimensionInputs() {
    var container = document.getElementById('estimator-dimension-inputs');
    if (!container) return;

    var def = LAYOUT_DATA[state.selections.layout];
    if (!def) { container.innerHTML = ''; return; }

    def.segments.forEach(function (seg) {
      if (!state.selections.measurements[seg.key]) {
        state.selections.measurements[seg.key] = 0;
      }
    });

    var html = def.segments.map(function (seg) {
      var val = state.selections.measurements[seg.key] || '';
      return [
        '<div class="estimator-field estimator-dim-field">',
          '<label for="est-dim-', seg.key, '">',
            '<span class="estimator-dim-key">', seg.key, '</span>',
            ' — ', seg.label, ' (mm)',
          '</label>',
          '<input type="number" id="est-dim-', seg.key, '"',
          ' data-dim-key="', seg.key, '"',
          ' value="', val > 0 ? val : '', '"',
          ' placeholder="e.g. 2400" min="0" step="1" inputmode="numeric">',
        '</div>',
      ].join('');
    }).join('');

    container.innerHTML = html;

    container.querySelectorAll('input[data-dim-key]').forEach(function (input) {
      input.addEventListener('input', function () {
        state.selections.measurements[input.dataset.dimKey] = parseFloat(input.value) || 0;
        updateAreaDisplay();
        var errEl = document.getElementById('estimator-step3-error');
        if (errEl) errEl.classList.add('estimator-step__error--hidden');
      });
    });

    updateAreaDisplay();
  }

  function updateAreaDisplay() {
    var resultEl = document.getElementById('estimator-area-result');
    if (!resultEl) return;

    var def = LAYOUT_DATA[state.selections.layout];
    if (!def) { resultEl.innerHTML = ''; return; }

    var sqm = def.calcArea(state.selections.measurements);
    sqm = Math.max(0, sqm);
    state.selections.areaSqm = Math.round(sqm * 100) / 100;

    if (state.selections.areaSqm > 0) {
      resultEl.innerHTML = [
        '<div class="estimator-area-display">',
          '<span class="estimator-area-display__label">Total area</span>',
          '<span class="estimator-area-display__value">',
            state.selections.areaSqm.toFixed(2),
            '<span class="estimator-area-display__unit"> m²</span>',
          '</span>',
        '</div>',
      ].join('');
    } else {
      resultEl.innerHTML = '';
    }
  }

  // ════════════════════════════════════════════════════════════
  // STEP 4 — Stone selection
  // ════════════════════════════════════════════════════════════
  function renderStep4() {
    if (state.products.length > 0) {
      renderStoneFilters();
      renderStoneGrid(filteredProducts());
    } else {
      fetchProducts();
    }
  }

  function fetchProducts() {
    var loadingEl = document.getElementById('estimator-stone-loading');
    var gridEl    = document.getElementById('estimator-stone-grid');
    if (loadingEl) loadingEl.innerHTML = '<div class="estimator-spinner"></div>';
    if (gridEl)    gridEl.innerHTML = '';

    fetch('https://duluxmarble.com.au/products.json?limit=250')
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (data) {
        state.products = (data.products || []).map(function (p) {
          return {
            id:      p.id,
            title:   p.title  || '',
            handle:  p.handle || '',
            tags:    Array.isArray(p.tags) ? p.tags : (p.tags ? p.tags.split(', ') : []),
            surface: extractSurface(p.body_html || ''),
            image:   (p.images && p.images[0]) ? p.images[0].src : null,
          };
        });
        if (loadingEl) loadingEl.innerHTML = '';
        renderStoneFilters();
        renderStoneGrid(filteredProducts());
      })
      .catch(function () {
        if (loadingEl) loadingEl.innerHTML = '';
        if (gridEl) {
          gridEl.innerHTML = [
            '<div class="estimator-fetch-error">',
              '<p>Unable to load products. Please visit ',
              '<a href="https://duluxmarble.com.au/collections/all" target="_blank" rel="noopener">',
              'duluxmarble.com.au/collections/all</a> to browse our full range,',
              ' or call us on <a href="tel:0407551514">0407 551 514</a>.',
              '</p>',
            '</div>',
          ].join('');
        }
      });
  }

  function extractSurface(html) {
    var text  = html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ');
    var match = text.match(/SURFACE:\s*(MATTE|VELVET|POLISHED)/i);
    return match ? match[1].toUpperCase() : null;
  }

  function filteredProducts() {
    if (state.activeFilter === 'all') return state.products;
    var filterDef = STONE_FILTERS.find(function (f) { return f.key === state.activeFilter; });
    if (!filterDef || !filterDef.test) return state.products;
    return state.products.filter(filterDef.test);
  }

  function renderStoneFilters() {
    var container = document.getElementById('estimator-stone-filters');
    if (!container) return;

    var html = STONE_FILTERS.map(function (f) {
      var isActive = state.activeFilter === f.key;
      return [
        '<button type="button" role="tab"',
        ' class="estimator-filter-btn', isActive ? ' estimator-filter-btn--active' : '', '"',
        ' data-filter="', f.key, '"',
        ' aria-selected="', isActive ? 'true' : 'false', '"',
        '>',
          f.label,
        '</button>',
      ].join('');
    }).join('');

    container.innerHTML = html;

    container.querySelectorAll('.estimator-filter-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.activeFilter = btn.dataset.filter;

        container.querySelectorAll('.estimator-filter-btn').forEach(function (b) {
          b.classList.remove('estimator-filter-btn--active');
          b.setAttribute('aria-selected', 'false');
        });
        btn.classList.add('estimator-filter-btn--active');
        btn.setAttribute('aria-selected', 'true');

        renderStoneGrid(filteredProducts());
      });
    });
  }

  function renderStoneGrid(products) {
    var container = document.getElementById('estimator-stone-grid');
    if (!container) return;

    if (!products || products.length === 0) {
      container.innerHTML = '<p class="estimator-empty">No stones found for this filter.</p>';
      return;
    }

    var html = products.map(function (p) {
      var isSelected = state.selections.stone && state.selections.stone.id === p.id;
      var imgHtml = p.image
        ? '<img src="' + p.image + '" alt="' + escapeHtml(p.title) + '" loading="lazy" class="estimator-stone-card__image">'
        : '<div class="estimator-stone-card__image estimator-stone-card__image--placeholder"></div>';
      var badgeHtml = p.surface
        ? '<span class="estimator-stone-card__badge">' + p.surface + '</span>'
        : '';

      return [
        '<button type="button"',
        ' class="estimator-stone-card', isSelected ? ' estimator-stone-card--selected' : '', '"',
        ' data-product-id="', p.id, '"',
        ' aria-pressed="', isSelected ? 'true' : 'false', '"',
        '>',
          imgHtml,
          '<div class="estimator-stone-card__info">',
            '<span class="estimator-stone-card__name">', escapeHtml(p.title), '</span>',
            badgeHtml,
          '</div>',
          '<div class="estimator-stone-card__check" aria-hidden="true">',
            '<svg width="11" height="9" viewBox="0 0 11 9" fill="none" xmlns="http://www.w3.org/2000/svg">',
              '<path d="M1 4L4 7.5L10 1" stroke="#0f0f0f" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>',
            '</svg>',
          '</div>',
        '</button>',
      ].join('');
    }).join('');

    container.innerHTML = html;

    container.querySelectorAll('.estimator-stone-card').forEach(function (card) {
      card.addEventListener('click', function () {
        var pid     = parseInt(card.dataset.productId);
        var product = state.products.find(function (p) { return p.id === pid; });
        if (!product) return;

        state.selections.stone = product;

        container.querySelectorAll('.estimator-stone-card').forEach(function (c) {
          c.classList.remove('estimator-stone-card--selected');
          c.setAttribute('aria-pressed', 'false');
        });
        card.classList.add('estimator-stone-card--selected');
        card.setAttribute('aria-pressed', 'true');

        var err = document.getElementById('estimator-step4-error');
        if (err) err.classList.add('estimator-step__error--hidden');

        setTimeout(function () { transitionToStep(4, 5); }, 180);
      });
    });
  }

  // ════════════════════════════════════════════════════════════
  // STEP 5 — Extras
  // ════════════════════════════════════════════════════════════
  function renderStep5() {
    renderExtrasGrid();
    renderEdgeProfiles();
  }

  function renderExtrasGrid() {
    var container = document.getElementById('estimator-extras-grid');
    if (!container) return;

    var html = EXTRAS_DATA.map(function (extra) {
      var qty = state.selections.extras[extra.key] || 0;
      return [
        '<div class="estimator-extras-card', qty > 0 ? ' estimator-extras-card--active' : '', '"',
        ' data-extra-key="', extra.key, '">',
          '<span class="estimator-extras-card__icon">', extra.icon, '</span>',
          '<span class="estimator-extras-card__label">', extra.label, '</span>',
          '<div class="estimator-qty" role="group" aria-label="Quantity of ', extra.label, '">',
            '<button type="button" class="estimator-qty__btn estimator-qty__btn--dec"',
            ' aria-label="Decrease ', extra.label, '" data-key="', extra.key, '">&#8722;</button>',
            '<span class="estimator-qty__count" id="est-qty-', extra.key, '">', qty, '</span>',
            '<button type="button" class="estimator-qty__btn estimator-qty__btn--inc"',
            ' aria-label="Increase ', extra.label, '" data-key="', extra.key, '">+</button>',
          '</div>',
        '</div>',
      ].join('');
    }).join('');

    container.innerHTML = html;

    container.querySelectorAll('.estimator-qty__btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var key     = btn.dataset.key;
        var current = state.selections.extras[key] || 0;
        var isDec   = btn.classList.contains('estimator-qty__btn--dec');

        current = isDec ? Math.max(0, current - 1) : Math.min(10, current + 1);
        state.selections.extras[key] = current;

        var countEl = document.getElementById('est-qty-' + key);
        if (countEl) countEl.textContent = current;

        var card = btn.closest('.estimator-extras-card');
        if (card) card.classList.toggle('estimator-extras-card--active', current > 0);
      });
    });
  }

  function renderEdgeProfiles() {
    var container = document.getElementById('estimator-edge-profiles');
    if (!container) return;

    var html = EDGE_PROFILE_DATA.map(function (profile) {
      var isSelected = state.selections.edgeProfile === profile.key;
      return [
        '<button type="button" class="estimator-edge-card', isSelected ? ' estimator-edge-card--selected' : '', '"',
        ' data-key="', profile.key, '"',
        ' aria-pressed="', isSelected ? 'true' : 'false', '"',
        '>',
          '<svg class="estimator-edge-card__svg" viewBox="0 0 72 34" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">',
            profile.svg,
          '</svg>',
          '<span class="estimator-edge-card__label">', profile.label, '</span>',
        '</button>',
      ].join('');
    }).join('');

    container.innerHTML = html;

    container.querySelectorAll('.estimator-edge-card').forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.selections.edgeProfile = btn.dataset.key;
        container.querySelectorAll('.estimator-edge-card').forEach(function (b) {
          b.classList.toggle('estimator-edge-card--selected', b === btn);
          b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
        });
      });
    });
  }

  // ════════════════════════════════════════════════════════════
  // STEP 6 — Summary + contact form
  // ════════════════════════════════════════════════════════════
  function renderStep6() {
    renderSummaryPanel();
    bindFormSubmit();
  }

  function renderSummaryPanel() {
    var container = document.getElementById('estimator-summary');
    if (!container) return;

    var s = state.selections;

    var stoneHtml = '';
    if (s.stone) {
      var imgHtml = s.stone.image
        ? '<img src="' + s.stone.image + '" alt="' + escapeHtml(s.stone.title) + '" class="estimator-summary__stone-img" loading="lazy">'
        : '<div class="estimator-summary__stone-img estimator-summary__stone-img--placeholder"></div>';
      stoneHtml = [
        '<div class="estimator-summary__stone">',
          imgHtml,
          '<div class="estimator-summary__stone-info">',
            '<span class="estimator-summary__stone-name">', escapeHtml(s.stone.title), '</span>',
            s.stone.surface ? '<span class="estimator-stone-card__badge">' + s.stone.surface + '</span>' : '',
          '</div>',
        '</div>',
      ].join('');
    }

    var rows = [
      { label: 'Space',        value: s.spaceType   ? capitalise(s.spaceType)  : '—' },
      { label: 'Layout',       value: s.layout      ? (LAYOUT_DATA[s.layout] ? LAYOUT_DATA[s.layout].label : capitalise(s.layout)) : '—' },
      { label: 'Measurements', value: formatMeasurements() },
      { label: 'Total area',   value: s.areaSqm > 0 ? s.areaSqm.toFixed(2) + ' m²' : '—' },
      { label: 'Extras',       value: formatExtras() },
      { label: 'Edge profile', value: s.edgeProfile ? capitalise(s.edgeProfile) : '—' },
    ];

    var rowsHtml = rows.map(function (row) {
      return [
        '<div class="estimator-summary__row">',
          '<span class="estimator-summary__row-label">', row.label, '</span>',
          '<span class="estimator-summary__row-value">', row.value, '</span>',
        '</div>',
      ].join('');
    }).join('');

    container.innerHTML = [
      '<p class="estimator-summary__title">YOUR SELECTIONS</p>',
      stoneHtml,
      '<div class="estimator-summary__list">', rowsHtml, '</div>',
    ].join('');
  }

  function bindFormSubmit() {
    var form = document.getElementById('estimator-form');
    if (!form || form.dataset.bound) return;
    form.dataset.bound = 'true';
    form.addEventListener('submit', function (e) { e.preventDefault(); handleFormSubmit(); });

    // Clear inline errors on input
    ['est-firstname', 'est-lastname', 'est-phone', 'est-email'].forEach(function (id) {
      var input = document.getElementById(id);
      if (!input) return;
      input.addEventListener('input', function () {
        input.classList.remove('estimator-field--invalid');
        var errEl = document.getElementById(id + '-error');
        if (errEl) errEl.style.display = 'none';
      });
    });

    var consent = document.getElementById('est-consent');
    if (consent) {
      consent.addEventListener('change', function () {
        var errEl = document.getElementById('est-consent-error');
        if (errEl) errEl.style.display = 'none';
      });
    }
  }

  function handleFormSubmit() {
    var firstNameEl = document.getElementById('est-firstname');
    var lastNameEl  = document.getElementById('est-lastname');
    var phoneEl     = document.getElementById('est-phone');
    var emailEl     = document.getElementById('est-email');
    var postcodeEl  = document.getElementById('est-postcode');
    var notesEl     = document.getElementById('est-notes');
    var consentEl   = document.getElementById('est-consent');
    var submitBtn   = document.getElementById('estimator-submit-btn');
    var formErrEl   = document.getElementById('estimator-form-error');

    // Clear previous validation state
    [firstNameEl, lastNameEl, phoneEl, emailEl].forEach(function (el) {
      if (el) el.classList.remove('estimator-field--invalid');
    });
    ['est-firstname-error', 'est-lastname-error', 'est-phone-error', 'est-email-error', 'est-consent-error'].forEach(function (id) {
      var el = document.getElementById(id);
      if (el) el.style.display = 'none';
    });
    if (formErrEl) formErrEl.classList.add('estimator-form-error--hidden');

    // Validate — collect all errors before returning
    var isValid = true;

    function markInvalid(inputEl, errorId) {
      if (inputEl) inputEl.classList.add('estimator-field--invalid');
      var errEl = document.getElementById(errorId);
      if (errEl) errEl.style.display = 'block';
      isValid = false;
    }

    if (!firstNameEl || !firstNameEl.value.trim())          markInvalid(firstNameEl, 'est-firstname-error');
    if (!lastNameEl  || !lastNameEl.value.trim())           markInvalid(lastNameEl,  'est-lastname-error');
    if (!phoneEl     || !phoneEl.value.trim())              markInvalid(phoneEl,     'est-phone-error');
    if (!emailEl     || !isValidEmail(emailEl.value.trim())) markInvalid(emailEl,    'est-email-error');
    if (!consentEl   || !consentEl.checked) {
      var consentErrEl = document.getElementById('est-consent-error');
      if (consentErrEl) consentErrEl.style.display = 'block';
      isValid = false;
    }

    if (!isValid) {
      // Scroll to first error
      var firstInvalid = document.querySelector('.estimator-field--invalid');
      if (firstInvalid) firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // Show loading state
    var labelEl   = submitBtn ? submitBtn.querySelector('.estimator-submit-btn__label')  : null;
    var spinnerEl = submitBtn ? submitBtn.querySelector('.estimator-submit-btn__spinner') : null;
    if (labelEl)   labelEl.style.display = 'none';
    if (spinnerEl) spinnerEl.classList.remove('estimator-submit-btn__spinner--hidden');
    if (submitBtn) submitBtn.disabled = true;

    var customer = {
      first_name: firstNameEl.value.trim(),
      last_name:  lastNameEl.value.trim(),
      phone:      phoneEl.value.trim(),
      email:      emailEl.value.trim(),
      postcode:   postcodeEl ? postcodeEl.value.trim() : '',
      notes:      notesEl    ? notesEl.value.trim()    : '',
    };

    var params = buildTemplateParams(customer);
    postToWebhook(customer);

    function onSuccess() {
      state.formSubmitted = true;
      if (typeof gtag === 'function') {
        gtag('event', 'conversion', { 'send_to': 'AW-16918195828/lN77CNmG7LAcEPTcnIM_' });
      }
      showSuccessState(customer.first_name);
    }

    function onError(err) {
      console.error('[Estimator] EmailJS error:', err);
      if (formErrEl) formErrEl.classList.remove('estimator-form-error--hidden');
      if (labelEl)   labelEl.style.display = '';
      if (spinnerEl) spinnerEl.classList.add('estimator-submit-btn__spinner--hidden');
      if (submitBtn) submitBtn.disabled = false;
    }

    if (typeof window.emailjs !== 'undefined' && config.emailjsServiceId) {
      try {
        Promise.all([
          window.emailjs.send(
            config.emailjsServiceId,
            config.emailjsInternalTemplateId,
            Object.assign({}, params, { to_email: 'info@duluxmarble.com.au' })
          ),
          window.emailjs.send(
            config.emailjsServiceId,
            config.emailjsCustomerTemplateId,
            params
          ),
        ])
          .then(onSuccess)
          .catch(onError);
      } catch (e) {
        onError(e);
      }
    } else {
      console.warn('[Estimator] EmailJS not configured. Params:', params);
      onSuccess();
    }
  }

  function buildTemplateParams(customer) {
    var s = state.selections;
    var now = new Date();
    var submissionTime = now.toLocaleString('en-AU', {
      timeZone: 'Australia/Sydney',
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });

    return {
      first_name:      customer.first_name,
      last_name:       customer.last_name,
      full_name:       customer.first_name + ' ' + customer.last_name,
      phone:           customer.phone,
      email:           customer.email,
      postcode:        customer.postcode || 'Not provided',
      notes:           customer.notes    || 'None',
      space_type:      s.spaceType  ? capitalise(s.spaceType)  : 'Not specified',
      layout:          s.layout     ? (LAYOUT_DATA[s.layout] ? LAYOUT_DATA[s.layout].label : capitalise(s.layout)) : 'Not specified',
      measurements:    formatMeasurements(),
      area_sqm:        s.areaSqm > 0 ? s.areaSqm.toFixed(2) + ' m²' : 'Not specified',
      stone_name:      s.stone      ? s.stone.title   : 'Not specified',
      stone_handle:    s.stone      ? s.stone.handle  : '',
      stone_finish:    s.stone && s.stone.surface ? s.stone.surface : 'Not specified',
      extras_list:     formatExtras(),
      edge_profile:    s.edgeProfile ? capitalise(s.edgeProfile) : 'Not specified',
      submission_time: submissionTime,
      step_reached:    String(state.currentStep),
      calendly_link:   'https://calendly.com/duluxmarble-info/30min',
    };
  }

  function postToWebhook(customer) {
    if (!config.webhookUrl) return;
    var s = state.selections;
    var payload = {
      firstName:    customer.first_name,
      lastName:     customer.last_name,
      phone:        customer.phone,
      email:        customer.email,
      postcode:     customer.postcode || '',
      areas:        (s.spaceType ? capitalise(s.spaceType) : '') + (s.layout && LAYOUT_DATA[s.layout] ? ' - ' + LAYOUT_DATA[s.layout].label : ''),
      measurements: formatMeasurements(),
      stone:        s.stone      ? s.stone.title            : '',
      finish:       s.stone && s.stone.surface ? s.stone.surface : '',
      thickness:    '',
      edgeProfile:  s.edgeProfile ? capitalise(s.edgeProfile) : '',
      totalSqm:     s.areaSqm > 0 ? s.areaSqm.toFixed(2)    : '',
      extras:       formatExtras(),
      notes:        customer.notes || '',
    };
    try {
      var qs = Object.keys(payload).map(function (k) {
        return encodeURIComponent(k) + '=' + encodeURIComponent(payload[k] || '');
      }).join('&');
      new Image().src = config.webhookUrl + '?' + qs;
    } catch (e) {}
  }

  function formatMeasurements() {
    var s   = state.selections;
    var def = s.layout ? LAYOUT_DATA[s.layout] : null;
    if (!def || !s.measurements) return 'Not specified';

    var parts = def.segments
      .filter(function (seg) { return (s.measurements[seg.key] || 0) > 0; })
      .map(function (seg) {
        return seg.key + ' (' + seg.label + '): ' + s.measurements[seg.key] + 'mm';
      });

    return parts.length > 0 ? parts.join(', ') : 'Not specified';
  }

  function formatExtras() {
    var items = EXTRAS_DATA
      .filter(function (e) { return (state.selections.extras[e.key] || 0) > 0; })
      .map(function (e) {
        var qty = state.selections.extras[e.key];
        return qty > 1 ? e.label + ' (' + qty + ')' : e.label;
      });
    return items.length > 0 ? items.join(', ') : 'None';
  }

  function showSuccessState(firstName) {
    var layout    = document.getElementById('estimator-step6-layout');
    var successEl = document.getElementById('estimator-success');
    var nav       = document.getElementById('estimator-nav');

    if (layout)    layout.classList.add('estimator-step6-layout--hidden');
    if (nav)       nav.classList.add('estimator-nav--hidden');

    if (successEl) {
      successEl.classList.remove('estimator-success--hidden');
      successEl.innerHTML = [
        '<div class="estimator-thankyou">',
          '<div class="estimator-thankyou__icon-wrap" aria-hidden="true">',
            '<svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">',
              '<path d="M5 14l6 6L23 8" stroke="#0f0f0f" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>',
            '</svg>',
          '</div>',
          '<h3 class="estimator-thankyou__heading">Thank you, ', escapeHtml(firstName || 'there'), '.</h3>',
          '<p class="estimator-thankyou__message">',
            'Our Alexandria team will review your selections and send a personalised quote within 1–2 business hours.',
            ' Check your inbox — and your spam folder, just in case.',
          '</p>',
          '<button type="button" class="estimator-thankyou__cta" onclick="Calendly.initPopupWidget({url:\'https://calendly.com/duluxmarble-info/30min\'});return false;">',
            'Book a consultation →',
          '</button>',
        '</div>',
      ].join('');
    }
  }

  // ════════════════════════════════════════════════════════════
  // NAVIGATION
  // ════════════════════════════════════════════════════════════
  function bindNavigation() {
    var backBtn = document.getElementById('estimator-back-btn');
    var nextBtn = document.getElementById('estimator-next-btn');

    if (backBtn) {
      backBtn.addEventListener('click', function () {
        if (state.currentStep > 1) transitionToStep(state.currentStep, state.currentStep - 1);
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        if (!validateCurrentStep()) return;
        if (state.currentStep < state.totalSteps) transitionToStep(state.currentStep, state.currentStep + 1);
      });
    }
  }

  function transitionToStep(from, to) {
    var fromEl = document.getElementById('estimator-step-' + from);
    var toEl   = document.getElementById('estimator-step-' + to);
    if (!fromEl || !toEl) { renderStep(to); return; }

    fromEl.classList.add('estimator-step--exiting');
    setTimeout(function () {
      fromEl.classList.remove('estimator-step--exiting');
      fromEl.classList.add('estimator-step--hidden');
      toEl.classList.remove('estimator-step--hidden');
      toEl.classList.add('estimator-step--entering');
      toEl.addEventListener('animationend', function () {
        toEl.classList.remove('estimator-step--entering');
      }, { once: true });
      renderStep(to);
      // Reset scroll position on every step change
      var scrollZone = document.querySelector('.estimator-scroll-zone');
      if (scrollZone) scrollZone.scrollTop = 0;
      window.scrollTo(0, 0);
    }, 180);
  }

  function validateCurrentStep() {
    switch (state.currentStep) {
      case 1:
        if (!state.selections.spaceType) {
          showStepError('estimator-step1-error');
          return false;
        }
        return true;

      case 2:
        if (!state.selections.layout) {
          showStepError('estimator-step2-error');
          return false;
        }
        return true;

      case 3: {
        var def = LAYOUT_DATA[state.selections.layout];
        if (!def) return true;
        var anyFilled = def.segments.some(function (seg) {
          return (state.selections.measurements[seg.key] || 0) > 0;
        });
        if (!anyFilled) {
          showStepError('estimator-step3-error', 'Please enter at least one measurement to continue.');
          return false;
        }
        if (state.selections.areaSqm < 0.5) {
          showStepError('estimator-step3-error', 'Please check your measurements — the area seems too small.');
          return false;
        }
        return true;
      }

      case 4:
        if (!state.selections.stone) {
          showStepError('estimator-step4-error');
          return false;
        }
        return true;

      case 5:
        return true; // extras are optional

      default:
        return true;
    }
  }

  function showStepError(id, message) {
    var el = document.getElementById(id);
    if (!el) return;
    if (message) el.textContent = message;
    el.classList.remove('estimator-step__error--hidden');
  }

  // ════════════════════════════════════════════════════════════
  // PROGRESS BAR
  // ════════════════════════════════════════════════════════════
  function updateProgressBar(step) {
    var labelEl = document.getElementById('estimator-progress-label');
    var fillEl  = document.getElementById('estimator-progress-fill');
    var trackEl = document.getElementById('estimator-progress-track');

    if (labelEl) {
      labelEl.innerHTML = [
        'Step ', step, ' of ', state.totalSteps,
        '<span class="estimator-progress__step-name" id="estimator-progress-step-name">',
        ' — ', STEP_NAMES[step],
        '</span>',
      ].join('');
    }

    if (trackEl) trackEl.setAttribute('aria-valuenow', step);

    if (fillEl) {
      var pct = ((step - 1) / (state.totalSteps - 1)) * 100;
      fillEl.style.width = pct + '%';
    }

    var motivatorEl = document.getElementById('estimator-progress-motivator');
    if (motivatorEl) {
      var msg = STEP_MOTIVATORS[step] || '';
      motivatorEl.classList.remove('estimator-motivator--in');
      void motivatorEl.offsetWidth; // force reflow to retrigger animation
      motivatorEl.textContent = msg;
      if (msg) motivatorEl.classList.add('estimator-motivator--in');
    }
  }

  function updateNavButtons(step) {
    var backBtn = document.getElementById('estimator-back-btn');
    var nextBtn = document.getElementById('estimator-next-btn');
    if (backBtn) backBtn.classList.toggle('estimator-nav__back--hidden', step === 1);
    if (nextBtn) nextBtn.classList.toggle('estimator-nav__next--hidden', step === 1 || step === 2 || step === 4 || step === state.totalSteps);
  }

  // ════════════════════════════════════════════════════════════
  // PARTIAL LEAD CAPTURE (page unload after step 3)
  // ════════════════════════════════════════════════════════════
  function setupPartialLeadTrigger() {
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState !== 'hidden') return;
      maybeSendPartialLead();
    });

    window.addEventListener('beforeunload', function () {
      maybeSendPartialLead();
    });
  }

  function maybeSendPartialLead() {
    if (state.currentStep < 4) return;
    if (state.partialLeadSent || state.formSubmitted) return;
    var lead = state.softLead || getSoftLead();
    if (!lead || !lead.email) return;
    state.partialLeadSent = true;
    sendPartialLeadEmail(lead);
  }

  function sendPartialLeadEmail(lead) {
    if (!config.emailjsServiceId || !config.emailjsInternalTemplateId) return;
    if (typeof window.emailjs === 'undefined') return;

    var s = state.selections;
    var params = {
      first_name:    lead.name  || '',
      last_name:     '',
      full_name:     lead.name  || '',
      phone:         '',
      email:         lead.email,
      postcode:      '',
      notes:         '',
      space_type:    s.spaceType  ? capitalise(s.spaceType)  : 'Not specified',
      layout:        s.layout     ? (LAYOUT_DATA[s.layout] ? LAYOUT_DATA[s.layout].label : capitalise(s.layout)) : 'Not specified',
      area_sqm:      s.areaSqm > 0 ? s.areaSqm.toFixed(2) + ' m²' : 'Not specified',
      stone_name:    s.stone      ? s.stone.title  : 'Not specified',
      stone_handle:  s.stone      ? s.stone.handle : '',
      stone_finish:  s.stone && s.stone.surface ? s.stone.surface : 'Not specified',
      extras_list:   formatExtras(),
      edge_profile:  s.edgeProfile ? capitalise(s.edgeProfile) : 'Not specified',
      thickness:     s.thickness  || 'Not specified',
      step_reached:  String(state.currentStep),
      is_partial:    'true',
      to_email:      'info@duluxmarble.com.au',
      calendly_link: 'https://calendly.com/duluxmarble-info/30min',
    };

    try {
      window.emailjs.send(config.emailjsServiceId, config.emailjsInternalTemplateId, params);
    } catch (e) { /* silent — user is leaving */ }
  }

  // ════════════════════════════════════════════════════════════
  // LOCALSTORAGE — soft lead
  // ════════════════════════════════════════════════════════════
  function saveSoftLead(lead) {
    try { localStorage.setItem('dulux_soft_lead', JSON.stringify(lead)); } catch (e) {}
  }

  function getSoftLead() {
    try {
      var raw = localStorage.getItem('dulux_soft_lead');
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  }

  // ════════════════════════════════════════════════════════════
  // UTILITIES
  // ════════════════════════════════════════════════════════════
  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  function capitalise(str) {
    if (!str) return '';
    return str.replace(/-/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  function escapeHtml(str) {
    if (!str) return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // ════════════════════════════════════════════════════════════
  // BOOT
  // ════════════════════════════════════════════════════════════
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

}());
